import { useState, useRef } from 'react';
import { Modal, Form, Input, Select, AutoComplete, Button, Tag, Typography, Popconfirm, Checkbox, Alert, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, StarOutlined } from '@ant-design/icons';
import { api } from '../../services/api';

const { Text } = Typography;

// Las provincias no cambian: se piden una vez por sesión.
let provinciasCache = null;
const cargarProvincias = async () => {
  if (!provinciasCache) {
    const res = await api.geo.provincias();
    if (res.status === 'success') provinciasCache = res.data || [];
  }
  return provinciasCache || [];
};

// Lo que se ve en la sugerencia: el departamento solo si aporta, porque hay
// muchas "San José" o "Santa Rosa" en una misma provincia.
const etiqueta = (l) => (l.departamento && l.departamento !== l.nombre ? `${l.nombre} (${l.departamento})` : l.nombre);

// Para comparar lo tipeado con la lista sin que pesen mayúsculas ni acentos.
const normalizar = (t) => (t || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const linea = (d) => {
  const extra = [d.piso && `piso ${d.piso}`, d.depto && `depto ${d.depto}`].filter(Boolean).join(' ');
  return `${d.calle} ${d.numero}${extra ? `, ${extra}` : ''}`;
};

const Fila = ({ children }) => (
  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>{children}</div>
);

// DomiciliosCliente son los lugares de entrega de un cliente, con quién recibe.
// Lo usan el perfil del cliente y su detalle en el admin.
//
// crear(data), editar(id, data) y quitar(id) devuelven la respuesta del server;
// sugerido precarga quién recibe en un domicilio nuevo.
export const DomiciliosCliente = ({ domicilios, crear, editar, quitar, sugerido = {} }) => {
  const [form] = Form.useForm();
  const [abierto, setAbierto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [inicial, setInicial] = useState({});
  const [provincias, setProvincias] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [buscando, setBuscando] = useState(false);
  // La localidad que eligió de las sugerencias. Si después edita el texto, deja
  // de valer y lo que quede escrito se guarda tal cual.
  const [elegida, setElegida] = useState(null);
  const busqueda = useRef(null);

  // Busca mientras se tipea, con una pausa para no pedir una por tecla.
  const buscarLocalidades = (idProvincia, q = '') => {
    clearTimeout(busqueda.current);
    if (!idProvincia) { setLocalidades([]); return; }
    busqueda.current = setTimeout(async () => {
      setBuscando(true);
      try {
        const res = await api.geo.localidades(idProvincia, q);
        if (res.status === 'success') setLocalidades(res.data || []);
      } finally {
        setBuscando(false);
      }
    }, q ? 250 : 0);
  };
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const abrir = async (d) => {
    setEditando(d || null);
    setError('');
    setProvincias(await cargarProvincias());
    setElegida(d?.id_localidad ? { id: d.id_localidad, texto: d.ciudad } : null);
    setLocalidades([]);
    if (d?.id_provincia) buscarLocalidades(d.id_provincia);
    // initialValues y no setFieldsValue: el form se monta recién al abrir.
    setInicial(d
      ? { ...d, piso: d.piso || '', depto: d.depto || '', obs: d.obs || '' }
      : { alias: '', calle: '', numero: '', piso: '', depto: '', id_provincia: undefined, id_localidad: undefined,
          ciudad: '', codigo_postal: '', obs: '', predeterminado: domicilios.length === 0, ...sugerido });
    setAbierto(true);
  };

  const guardar = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    // Si eligió una sugerencia, va con su id. Si escribió el nombre exacto de
    // una pero no la tocó, se la reconoce igual. Si no, queda lo escrito.
    let idLocalidad = elegida && elegida.texto === values.ciudad ? elegida.id : null;
    if (!idLocalidad) {
      const iguales = localidades.filter((l) =>
        normalizar(l.nombre) === normalizar(values.ciudad) || normalizar(etiqueta(l)) === normalizar(values.ciudad));
      if (iguales.length === 1) idLocalidad = iguales[0].id;
    }
    const datos = { ...values, id_localidad: idLocalidad };
    setGuardando(true);
    setError('');
    try {
      const res = editando ? await editar(editando.id, datos) : await crear(datos);
      if (res.status === 'success') {
        message.success(editando ? 'Domicilio guardado' : 'Domicilio agregado');
        setAbierto(false);
      } else {
        setError(res.message || 'No se pudo guardar el domicilio');
      }
    } finally {
      setGuardando(false);
    }
  };

  const hacerPrincipal = async (d) => {
    const res = await editar(d.id, { ...d, piso: d.piso || '', depto: d.depto || '', obs: d.obs || '', predeterminado: true });
    if (res.status === 'success') message.success('Ahora es el domicilio principal');
    else message.error(res.message || 'No se pudo cambiar');
  };

  const sacar = async (d) => {
    const res = await quitar(d.id);
    if (res.status === 'success') message.success('Domicilio quitado');
    else message.error(res.message || 'No se pudo quitar');
  };

  const requerido = (msg) => [{ required: true, whitespace: true, message: msg }];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {domicilios.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 13 }}>Todavía no hay domicilios cargados.</Text>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {domicilios.map((d, i) => (
            <div key={d.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0',
              borderTop: i === 0 ? 'none' : '1px solid #f0f0f0',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Text strong style={{ fontSize: 14 }}>{d.alias}</Text>
                  {d.predeterminado && <Tag color="blue">Principal</Tag>}
                </div>
                <div style={{ fontSize: 13 }}>{linea(d)}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {d.ciudad}, {d.provincia} ({d.codigo_postal}) · recibe {d.nombre} {d.apellido}
                </Text>
              </div>
              <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                {!d.predeterminado && (
                  <Button type="text" icon={<StarOutlined />} onClick={() => hacerPrincipal(d)}
                    aria-label="Usar como principal" title="Usar como principal" />
                )}
                <Button type="text" icon={<EditOutlined />} onClick={() => abrir(d)}
                  aria-label="Editar domicilio" title="Editar" />
                <Popconfirm title="¿Quitar este domicilio?" okText="Quitar" cancelText="Cancelar"
                  onConfirm={() => sacar(d)}>
                  <Button type="text" danger icon={<DeleteOutlined />} aria-label="Quitar domicilio" title="Quitar" />
                </Popconfirm>
              </div>
            </div>
          ))}
        </div>
      )}

      <div>
        <Button icon={<PlusOutlined />} onClick={() => abrir(null)}>Agregar domicilio</Button>
      </div>

      <Modal
        open={abierto}
        title={editando ? 'Editar domicilio' : 'Nuevo domicilio'}
        okText="Guardar"
        onOk={guardar}
        onCancel={guardando ? undefined : () => setAbierto(false)}
        confirmLoading={guardando}
        width={560}
        destroyOnHidden
      >
        {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}
        <Form form={form} layout="vertical" preserve={false} initialValues={inicial}>
          <Form.Item label="Nombre del domicilio" name="alias" extra="Por ejemplo Casa o Oficina. Si lo dejás vacío se usa la calle.">
            <Input />
          </Form.Item>
          <Fila>
            <Form.Item label="Calle" name="calle" rules={requerido('Falta la calle')} style={{ flex: '3 1 220px' }}>
              <Input />
            </Form.Item>
            <Form.Item label="Número" name="numero" rules={requerido('Falta el número')} style={{ flex: '1 1 90px' }}>
              <Input />
            </Form.Item>
          </Fila>
          <Fila>
            <Form.Item label="Piso" name="piso" style={{ flex: '1 1 90px' }}><Input /></Form.Item>
            <Form.Item label="Depto" name="depto" style={{ flex: '1 1 90px' }}><Input /></Form.Item>
            <Form.Item label="Código postal" name="codigo_postal" rules={requerido('Falta el código postal')} style={{ flex: '1 1 120px' }}>
              <Input />
            </Form.Item>
          </Fila>
          <Fila>
            <Form.Item label="Provincia" name="id_provincia" rules={[{ required: true, message: 'Elegí la provincia' }]}
              style={{ flex: '1 1 200px' }}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Elegí la provincia"
                options={provincias.map((p) => ({ value: p.id, label: p.nombre }))}
                onChange={(id) => {
                  form.setFieldsValue({ ciudad: '' });
                  setElegida(null);
                  buscarLocalidades(id);
                }}
              />
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(x, y) => x.id_provincia !== y.id_provincia}>
              {({ getFieldValue }) => (
                <Form.Item label="Localidad" name="ciudad" rules={requerido('Falta la localidad')}
                  style={{ flex: '1 1 200px' }}
                  extra="Si no aparece en la lista, escribila igual.">
                  <AutoComplete
                    disabled={!getFieldValue('id_provincia')}
                    placeholder={getFieldValue('id_provincia') ? 'Escribí la localidad' : 'Primero la provincia'}
                    onSearch={(q) => buscarLocalidades(getFieldValue('id_provincia'), q)}
                    onFocus={() => { if (!localidades.length) buscarLocalidades(getFieldValue('id_provincia')); }}
                    onSelect={(_, opcion) => setElegida({ id: opcion.id, texto: opcion.value })}
                    notFoundContent={buscando ? 'Buscando…' : null}
                    options={localidades.map((l) => ({ value: etiqueta(l), id: l.id, key: l.id }))}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Fila>
          <Text strong style={{ display: 'block', margin: '4px 0 8px', fontSize: 13 }}>Quién recibe</Text>
          <Fila>
            <Form.Item label="Nombre" name="nombre" rules={requerido('Falta el nombre')} style={{ flex: '1 1 160px' }}>
              <Input />
            </Form.Item>
            <Form.Item label="Apellido" name="apellido" rules={requerido('Falta el apellido')} style={{ flex: '1 1 160px' }}>
              <Input />
            </Form.Item>
          </Fila>
          <Fila>
            <Form.Item label="DNI" name="dni" rules={requerido('Falta el DNI')} style={{ flex: '1 1 140px' }}>
              <Input inputMode="numeric" />
            </Form.Item>
            <Form.Item label="Teléfono de contacto" name="telefono_contacto" rules={requerido('Falta un teléfono')} style={{ flex: '1 1 160px' }}>
              <Input />
            </Form.Item>
          </Fila>
          <Form.Item label="Indicaciones para la entrega" name="obs">
            <Input.TextArea rows={2} placeholder="Timbre, horarios, entre calles…" />
          </Form.Item>
          <Form.Item name="predeterminado" valuePropName="checked" style={{ marginBottom: 0 }}>
            <Checkbox disabled={editando?.predeterminado}>Usar como domicilio principal</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
